/**
 * La cabina luce: il capo che si sta ricevendo, in grande.
 */

import { useState } from 'react';
import type { GenereArticolo, LineView } from '@errebi/domain';

/**
 * Il capo in cabina luce, con tre ripieghi in fila.
 *
 * **1. La foto nostra**, scattata al banco su questa esatta variante (regola
 * 38-bis). Vince su tutto, e la ragione è la stessa per cui si scatta: mostra
 * il capo **com'è**, non un rendering.
 *
 * **2. La vista configurata** di DecoNetwork — il capo base col grafico
 * applicato. Alla ricezione il capo è ancora grezzo, quindi mostra il modello e
 * il colore giusti e una stampa che sul pezzo in mano non c'è: serve a
 * riconoscere l'articolo, non a controllare la decorazione.
 *
 * **3. Il disegno.** Non è un caso raro da tollerare, è la metà dei capi:
 * borracce, calzature e materiale a metro non hanno viste, e senza credenziali
 * il catalogo di prova non ha miniature affatto. È esattamente il vuoto che le
 * foto del banco esistono per riempire.
 *
 * **Un'immagine che non carica scende al ripiego successivo**, non salta in
 * fondo: lo stato ricorda **l'indirizzo che ha fallito**, non un booleano, così
 * una foto nostra sparita dal disco lascia comparire l'anteprima invece del
 * disegno. Ricordare un booleano era il difetto della versione con due soli
 * rami, e con tre si vedrebbe. Lo stato si azzera da sé al cambio di indirizzo,
 * o il capo dopo erediterebbe il fallimento di quello prima.
 */
export function CapoInCabina({
  riga,
  className = 'booth__capo',
}: {
  riga: LineView;
  /**
   * La classe è l'unica cosa che cambia fra i due posti in cui questo capo
   * compare: grande in cabina luce, piccolo nelle righe dell'elenco articoli.
   * I tre ripieghi si comportano uguale in tutti e due — che è la ragione per
   * cui la miniatura non è stata ricopiata di là.
   */
  className?: string;
}) {
  return (
    <GarmentPicture
      foto={riga.foto}
      anteprima={riga.anteprima}
      colore={riga.colore}
      genere={riga.genere}
      alt={`${riga.sku} ${riga.colore}`}
      className={className}
    />
  );
}

/**
 * I tre ripieghi, staccati dalla cabina luce perché li vuole anche il telefono.
 *
 * La pagina che il QR apre fa la stessa domanda della cabina — «è questo il
 * capo?» — a chi sta in magazzino col foglio in mano, e una seconda catena di
 * ripieghi scritta altrove avrebbe cominciato a divergere alla prima modifica.
 * Cambia solo da dove arrivano i campi: lì una riga al banco, qui un'etichetta
 * scritta e archiviata.
 */
export function GarmentPicture({
  foto,
  anteprima,
  colore,
  genere,
  alt,
  className = 'booth__capo',
}: {
  // Non opzionali ma **nullabili**: con `exactOptionalPropertyTypes` un campo
  // `?` non accetta un `undefined` scritto a mano, e i due chiamanti leggono
  // proprio campi che possono mancare. Dichiararli così dice la verità — «può
  // non esserci» — invece di far scrivere spread condizionali a chi chiama.
  foto: string | null | undefined;
  anteprima: string | null | undefined;
  colore: string;
  genere: GenereArticolo | null | undefined;
  alt: string;
  className?: string;
}) {
  const [rotte, setRotte] = useState<readonly string[]>([]);

  // `typeof === 'string'` e non `!== null`: il tipo promette `string | null`,
  // ma uno skew di deploy — web nuovo, API vecchia — fa arrivare il campo
  // **assente**, e un `undefined` passato a `src` renderebbe un'immagine rotta
  // al posto del disegno. È lo stesso difensivo che `receiving.tsx` applica a
  // `v.annullamento`, e per la stessa ragione.
  const mostrata = [foto, anteprima].find(
    (i): i is string => typeof i === 'string' && !rotte.includes(i),
  );

  if (mostrata === undefined) {
    return <CapoDisegnato colore={colore} genere={genere ?? null} className={className} />;
  }

  return (
    <img
      className={className}
      src={mostrata}
      alt={alt}
      onError={() => setRotte((gia) => [...gia, mostrata])}
    />
  );
}

/**
 * Le parole con cui un colore si dichiara, **nelle due lingue**.
 *
 * Il nome del colore arriva da DecoNetwork come lo scrive il fornitore, e i
 * fornitori esteri scrivono in inglese: sul solo ordine 607095, misurato il
 * 17/08/2026, «Black», «White», «Grey Check» e «Yellow/Yellow» stanno accanto a
 * «Nero» e «Bianco». Cercare le sole parole italiane non è un dettaglio di
 * traduzione — faceva uscire **bianco sporco un pantalone nero**, cioè un
 * disegno che contraddice il capo che l'operatrice ha in mano.
 *
 * Il nero si prova per primo: su un capo bicolore («Blu/Nero») qualunque delle
 * due tinte è un'approssimazione, ma va scelta sempre la stessa, o l'ordine
 * delle parole deciderebbe il colore.
 */
const TINTE: readonly (readonly [RegExp, string])[] = [
  [/nero|black/, '#1b1716'],
  [/blu|blue|navy|royal/, '#1f3f8f'],
];

/** Tutto ciò che non si riconosce: un ripiego dichiarato, non una tinta. */
const CHIARA = '#f2f2f0';

/**
 * Le sagome, una per genere.
 *
 * Fino al 27/08/2026 ce n'era una sola — la maglia — e usciva anche per le
 * scarpe: il difetto si vedeva ricevendo la SZ500B FLEXIFLY dell'ordine 606913,
 * merce neutra e quindi senza viste configurate, che in cabina luce compariva
 * come una t-shirt blu. Sui settanta articoli che passano dal banco, diciassette
 * non li pubblica nemmeno lo storefront: per quelli la sagoma **è** tutto ciò
 * che si può disegnare, e vale la pena che sia quella giusta.
 *
 * Sono profili grossolani di proposito. Non devono somigliare all'articolo:
 * devono dire *che cosa* è, da due metri, a chi ha le mani occupate.
 */
const SAGOME: Readonly<Record<GenereArticolo, string>> = {
  capo: 'M40 12 L60 22 L80 12 L108 30 L96 52 L86 46 L86 130 L34 130 L34 46 L24 52 L12 30 Z',
  // Vista di profilo, punta a destra: è il modo in cui una scarpa si riconosce
  // subito, mentre di fronte somiglia a qualunque altra cosa.
  calzatura:
    'M18 60 L34 60 L44 78 L74 88 L104 98 Q112 101 112 110 L112 118 L18 118 Q12 118 12 112 L12 66 Q12 60 18 60 Z',
  pantalone: 'M38 14 L82 14 L88 60 L78 128 L64 128 L60 74 L56 128 L42 128 L32 60 Z',
  // Calotta più visiera: distingue il cappellino dal resto senza pretese.
  copricapo: 'M26 84 Q26 34 60 34 Q94 34 94 84 L108 84 Q116 84 116 96 L26 96 Z',
  // Una sacca con il manico: vale per zaini, borse, borracce e portabadge.
  accessorio: 'M40 44 L40 32 Q40 20 60 20 Q80 20 80 32 L80 44 L96 44 L104 126 L16 126 L24 44 Z',
};

/**
 * Il capo disegnato: il ripiego di chi una miniatura non ce l'ha.
 *
 * È SVG per la stessa ragione del prototipo: il colore mostrato è esattamente
 * quello dichiarato, senza che una compressione o una luce lo spostino. Le tre
 * tinte non pretendono di essere una tavolozza: tutto ciò che non è nero o blu
 * esce bianco sporco.
 *
 * Genere e colore sono **indipendenti**: una scarpa nera resta nera. Farli
 * dipendere l'uno dall'altro darebbe tutte le calzature dello stesso colore.
 */
export function CapoDisegnato({
  colore,
  genere,
  className = 'booth__capo',
}: {
  colore: string;
  genere: GenereArticolo | null;
  className?: string;
}) {
  const detto = colore.toLowerCase();
  const tinta = TINTE.find(([parole]) => parole.test(detto))?.[1] ?? CHIARA;
  const forma = SAGOME[genere ?? 'capo'];
  return (
    <svg
      className={className}
      viewBox="0 0 120 140"
      role="img"
      aria-label={`${genere ?? 'capo'} ${colore}`}
    >
      <path d={forma} fill={tinta} stroke="#15171a" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
